<template>
  <div
    class="py-30 lg:py-40 xl:pt-50"
    :class="{
      'bg-primary-100': bgPrimary,
      'bg-blue-900 print:!bg-white': bgBlue,
    }"
  >
    <div class="container mb-20 md:mb-30">
      <h3
        v-if="title"
        class="font-bold text-2xl md:text-3xl lg:text-4xl xl:text-5xl print:text-xl print:!text-body hyphens-auto"
        :class="{
          'text-blue-900': !bgPrimary && !bgBlue,
          'text-primary-500': bgPrimary,
          'text-white': bgBlue,
        }"
      >
        {{ title }}
      </h3>
    </div>

    <div class="container mobile-only:px-0">
      <Carousel
        ref="carouselComponent"
        class="relative text-center touch-pan-y overscroll-none"
        :items-to-show="1"
        :wrap-around="wrapAround"
      >
        <slot />
        <!--
        Usage example:

        <slide v-for="(slide, key) in slides" :key="key">
          <ImageSlide
            :total-slides="slides.length"
            :label="slide.label"
            :copyright="slide.copyright"
            :current-slide-number="key + 1 "
          >
            <img :src="slide.img">
          </ImageSlide>
        </slide>
      -->
        <template v-if="hasPagination" #addons>
          <div
            class="absolute top-0 inset-x-0 flex pointer-events-none aspect-video justify-between"
          >
            <button
              class="group/button carousel-button"
              :aria-label="$texts('gallery.previousSlide', 'Vorheriges Bild')"
              @click.capture="prevSlide"
            >
              <div class="button is-prev mobile-only:is-sm is-icon-only" />
            </button>

            <button
              class="group/button carousel-button"
              :aria-label="$texts('gallery.nextSlide', 'Nächstes Bild')"
              @click.capture="nextSlide"
            >
              <div class="button is-next mobile-only:is-sm is-icon-only" />
            </button>
          </div>
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Carousel } from 'vue3-carousel'

defineProps<{
  bgPrimary?: boolean
  bgBlue?: boolean
  sidebarTitle?: string
  title?: string
  wrapAround?: boolean
  hasPagination?: boolean
}>()

const carouselComponent = ref<typeof Carousel | null>(null)

const nextSlide = () => {
  carouselComponent.value?.next()
}
const prevSlide = () => {
  carouselComponent.value?.prev()
}
</script>

<style lang="postcss">
@tailwind components;

@layer components {
  .carousel-button {
    @apply px-25 md:px-15 flex items-center cursor-pointer pointer-events-auto relative overflow-hidden;

    @screen md {
      &:before {
        @apply content-empty absolute top-0 bottom-0;
        @apply from-body/40 to-body/0;
        @apply transition ease-swing duration-300 will-change-[transform,opacity];
        @apply opacity-0;
      }
      &:hover:before {
        @apply opacity-100;
      }

      &:first-child:before {
        @apply -left-1 right-0;
        @apply bg-gradient-to-r -translate-x-30 hover:!translate-x-0;
      }

      &:last-child:before {
        @apply left-0 -right-1;
        @apply bg-gradient-to-l translate-x-30 hover:!translate-x-0;
      }
    }
  }

  .carousel.is-dragging {
    @apply touch-none;
  }

  .carousel__track {
    @apply flex relative p-0;
  }

  .carousel__viewport {
    @apply overflow-hidden;
  }

  .carousel__sr-only {
    @apply sr-only;
  }

  .carousel__slide {
    @apply snap-always flex-shrink-0 m-0 relative flex justify-center items-start;
    @apply px-15 md:px-0;

    /* Fix iOS scrolling #22 */
    transform: translateZ(0);
  }

  /* Fake a gap between slides. */
  .carousel__slide--prev {
    @apply md:-translate-x-15;
  }
  .carousel__slide--next {
    @apply md:translate-x-15;
  }

  @media print {
    .carousel__track {
      @apply flex-wrap !transform-none;
    }

    .carousel__slide {
      @apply !w-[50%] !h-auto !items-start;
    }

    .carousel__container__nav__bar {
      @apply hidden;
    }

    .carousel__image-slide {
      @apply !h-auto p-20;
    }
  }
}
</style>
